import { useId, useRef, useState, type DragEvent } from "react";
import { cn } from "@/lib/utils";

const ACCEPT = ".pdf,.xlsx,.xls,.csv,.bax";
const MAX_FILES_PER_PICK = 100;

interface FileDropZoneProps {
  onFiles: (files: File[]) => void;
}

// Soft Modern drop zone — rounded, pastel, friendly. Both click and drop.
export function FileDropZone({ onFiles }: FileDropZoneProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleSelected = (list: FileList | null) => {
    if (!list) return;
    const files = Array.from(list).slice(0, MAX_FILES_PER_PICK);
    if (files.length === 0) return;
    onFiles(files);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragging(false);
    handleSelected(e.dataTransfer.files);
  };

  const handleDragOver = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    if (!dragging) setDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragging(false);
  };

  return (
    <label
      htmlFor={inputId}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={cn(
        "group relative block cursor-pointer rounded-2xl border-2 border-dashed",
        "px-8 py-14 text-center transition-all",
        dragging
          ? "border-primary bg-primary-soft scale-[1.005] shadow-soft"
          : "border-rule-strong bg-surface/60 hover:border-primary hover:bg-primary-soft/40",
      )}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={ACCEPT}
        multiple
        className="sr-only"
        onChange={(e) => handleSelected(e.target.files)}
      />

      <CloudGlyph />

      <div className="mt-4 text-[1.3rem] font-medium text-ink">
        Drop files here or{" "}
        <span className="text-primary underline decoration-primary/30 decoration-2 underline-offset-4">
          click to browse
        </span>
      </div>
      <div className="mt-2 text-[0.92rem] text-ink-muted">
        Supports multiple files · PDF, Excel, CSV, BAX · 100MB each
      </div>
    </label>
  );
}

function CloudGlyph() {
  return (
    <svg
      width="56"
      height="56"
      viewBox="0 0 56 56"
      fill="none"
      aria-hidden
      className="mx-auto"
    >
      <circle cx="28" cy="28" r="28" className="fill-primary-soft" />
      <path
        d="M18 36h20a7 7 0 0 0 1.2-13.9A11 11 0 0 0 17 24a6 6 0 0 0 1 12z"
        className="stroke-primary"
        strokeWidth="1.6"
        fill="none"
      />
      <path
        d="M28 32V20m0 0l-4 4m4-4l4 4"
        className="stroke-primary"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
